/*
 *
 * File: PassengerAcceptBookingsScreen.js
 *
 * Purpose: Passenger accept bookings screen in passengers protected area.
 *
 * Author:   Thomas Burgard (Software development)
 * Created:  19.04.2023
 * Modified: 19.04.2023
 *
 * Copyright (c) DrivePartner GmbH 2022
 *
 */

import { React, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Paginator } from "primereact/paginator";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { toast } from "react-toastify";
import { germanDate, getUserRegistrationData } from "../../utils/utils";
import { getPassengerBookings } from "../../store/actions";
import {
  getUserFromLocalStorage,
  getPassengerDataFromLocalStorage,
} from "../../utils/localStorage";

//--------------------------------------------------------------
//--- Variable declarations ------------------------------------
//--------------------------------------------------------------
const URL_GOOGLE_MAPS = "http://www.google.com/maps/search/?api=1&query=";
let userRegistrationDataObj = null;
let numberOfDatasets = 0;
//---End component variable declarations -------------------------

/**
 * Implementation of passenger accept booking list screen component.
 *
 * @param {*} props
 * @returns JSX-HTML-DOM
 */
const PassengerAcceptBookingsScreen = (props) => {
  const { t, i18n } = useTranslation("common");
  const userData = props.userData;
  const username = getUserFromLocalStorage();
  const passengerData = getPassengerDataFromLocalStorage();

  const month = [
    /*{ name: t("screentext.screen-text-month-all"), code: "all" },
    { name: t("screentext.screen-text-month-jan"), code: "01" },
    { name: t("screentext.screen-text-month-feb"), code: "02" },
    { name: t("screentext.screen-text-month-mar"), code: "03" },
    { name: t("screentext.screen-text-month-apr"), code: "04" },
    { name: t("screentext.screen-text-month-may"), code: "05" },
    { name: t("screentext.screen-text-month-jun"), code: "06" },
    { name: t("screentext.screen-text-month-jul"), code: "07" },
    { name: t("screentext.screen-text-month-aug"), code: "08" },
    { name: t("screentext.screen-text-month-sep"), code: "09" },
    { name: t("screentext.screen-text-month-oct"), code: "10" },
    { name: t("screentext.screen-text-month-nov"), code: "11" },
    { name: t("screentext.screen-text-month-dec"), code: "12" },*/
    { name: "01-12", code: "all" },
    { name: "01", code: "01" },
    { name: "02", code: "02" },
    { name: "03", code: "03" },
    { name: "04", code: "04" },
    { name: "05", code: "05" },
    { name: "06", code: "06" },
    { name: "07", code: "07" },
    { name: "08", code: "08" },
    { name: "09", code: "09" },
    { name: "10", code: "10" },
    { name: "11", code: "11" },
    { name: "12", code: "12" },
  ];

  //--------------------------------------------------------------
  //--- REDUX ----------------------------------------------------
  //--------------------------------------------------------------
  const dispatch = useDispatch();
  const passengerBookingServerData = useSelector(
    (state) => state.bookingReducer
  );

  //--------------------------------------------------------------
  // Component states
  //--------------------------------------------------------------
  const [filteredList, setFilteredList] = new useState([]);
  const [serverPending, setServerPending] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [year, setYear] = useState(new Date().getFullYear());
  const [bookingDetailsVisible, setBookingDetailsVisible] = useState(false);  
  const [selectedMonth, setSelectedMonth] = useState(null);      
  const bookingDetails = useRef(null);
  //--- End states -----------------------------------------------

  userRegistrationDataObj = getUserRegistrationData(userData);

  //--------------------------------------------------------------
  //--- Will be called after "first" rendering! ------------------
  //--------------------------------------------------------------
  useEffect(() => {
    const passengerID = getPassengerDataFromLocalStorage().ID;

    setServerPending(true); // For showing progress spinner!
    dispatch(
      getPassengerBookings(
        year,
        "",
        passengerID,
        userRegistrationDataObj.userID,
        0,
        100
      )
    );
  }, []);

  //--------------------------------------------------------------
  //--- Called after "passengerBookingServerData" changed
  //--------------------------------------------------------------
  useEffect(() => {
    setTotalRecords(0);
    numberOfDatasets = 0;
    // "Data received" -> Look into bookings_reducer.js
    if (
      passengerBookingServerData.bookingData.BookingData === "Data received"
    ) {
      setServerPending(false);

      if (passengerBookingServerData.passengerBookings.Success) {
        setTotalRecords(
          passengerBookingServerData.passengerBookings.NumberOfDatasets
        );
        setFilteredList(
          passengerBookingServerData.passengerBookings.BookingData
        );
        numberOfDatasets =
          passengerBookingServerData.passengerBookings.NumberOfDatasets;
      } else {
        toast.error(t("errortext.error-text-server-error"));
      }
    }
  }, [passengerBookingServerData]);

  /**
   * View From- & To-Location on Google maps in a new browser tab.
   * @param {*} urlStr Location URL-String
   */
  const viewLocationOnGoogleMaps = (urlStr) => {
    window.open(URL_GOOGLE_MAPS + urlStr);
  };

  /**
   * View booking result with given ticketID.
   */
  const viewBookingDetails = () => {};

  /**
   * Handle search item input change event.
   *
   * @param {*} event form event
   */
  const handleChange = (event) => {
    const nameField = event.target.name;
    const input = event.target.value;
    let localYear = "";
    let localMonth = "";
    const passengerID = getPassengerDataFromLocalStorage().ID;

    if (nameField === "year") {
      localYear = input;
      setYear(localYear);
      setFirst(0);
      setRows(100);
      setServerPending(true); // For showing progress spinner!
      dispatch(
        getPassengerBookings(
          localYear,
          localMonth,
          passengerID,
          userRegistrationDataObj.userID,
          first,
          rows
        )
      );    
    } else {
      localMonth = input.code;
      setFirst(0);
      setRows(100);
      setSelectedMonth(event.value)
      setServerPending(true); // For showing progress spinner!
      dispatch(
        getPassengerBookings(
          year,
          localMonth,
          passengerID,
          userRegistrationDataObj.userID,
          first,
          rows
        )
      );
    }
  };

  /**
   * Handle paginator page change event.
   *
   * @param {*} event form event
   */
  const handlePageChange = (event) => {
    const passengerID = getPassengerDataFromLocalStorage().ID;
    setFirst(event.first);
    setRows(event.rows);

    setServerPending(true); // For showing progress spinner!
    dispatch(
      getPassengerBookings(
        year,
        "",
        passengerID,
        userRegistrationDataObj.userID,
        event.first,
        event.rows
      )
    );
  };

  /**
   * Show the selected booking data via "Dialog".
   *
   * @param {*} bookingData booking data object
   */
  const showDetailsDialog = (bookingData) => {
    bookingDetails.current = bookingData; // No rerendering necessary with useRef!
    setBookingDetailsVisible(true);
  };

  /**
   * Change selected booking with given booking object.
   *
   * @param {*} booking Booking object
   */
  const changeBooking = (booking) => {};

  /**
   * Show booking boxes on screen.
   *
   * @param {*} item Array item
   * @param {*} index Index of current array item
   * @returns JSX-Code for booking list
   */
  const showBookings = (item, index) => {
    const pickupDate = germanDate(item.PickupDate);

    // Remove the last three chars from time
    const pickupTime = item.PickupTime.substring(0, item.PickupTime.length - 3);

    return (
      <>
        <li
          key={index}
          className="flex flex-column md:flex-row md:align-items-center md:justify-content-between p-3 border-1 mb-3 bg-primary-50 border-primary-500 text-primary-500"
          style={{ borderRadius: "4px" }}
        >
          <div>
            <div className="font-bold">
              <i className="pi pi-calendar" />
              <span className="mr-2"> {pickupDate}</span>
              <i className="pi pi-clock" />
              <span className="mr-2"> {pickupTime}</span>
              <span className="mr-2">TicketID: {item.TicketID}</span>
            </div>
            <p className="mt-3 mb-0">
              <span className="font-bold">
                {t("screentext.screen-text-from")}
              </span>{" "}
              <i
                className="pi pi-map-marker"
                onClick={() => viewLocationOnGoogleMaps(item.PickupFrom)}
                style={{ cursor: "pointer" }}
              />{" "}
              {item.PickupFrom}
            </p>
            <p className="mt-2 mb-0">
              <span className="font-bold">
                {t("screentext.screen-text-to")}
              </span>{" "}
              <i
                className="pi pi-map-marker"
                onClick={() => viewLocationOnGoogleMaps(item.TransferTo)}
                style={{ cursor: "pointer" }}
              />{" "}
              {item.TransferTo}
            </p>
          </div>
          <div className="flex align-items-center justify-content-between md:justify-content-end mt-3 md:mt-0">
            <span
              className="bg-primary-400 text-primary-50 font-medium py-2 px-3"
              style={{ borderRadius: "4px", cursor: "pointer" }}
              onClick={() => showDetailsDialog(item)}
            >
              <i className="pi pi-external-link" />{" "}
              {t("screentext.screen-text-passenger-booking-details")}
            </span>
            <span
              className="bg-primary-400 text-primary-50 font-medium py-2 px-3 ml-2"
              style={{ borderRadius: "4px", cursor: "pointer" }}
              onClick={() => changeBooking(item)}
            >
              <i className="pi pi-pencil" />{" "}
              {t("screentext.screen-text-change")}
            </span>
          </div>
        </li>
      </>
    );
  };

  /**
   * Show booking details data in dialog.
   *
   * @returns JSX-Code for showing booking data in dialog component
   */
  const showBookingDetails = () => {
    const pickupDate = germanDate(bookingDetails.current.PickupDate);

    // Remove the last three chars from time
    const pickupTime = bookingDetails.current.PickupTime.substring(
      0,
      bookingDetails.current.PickupTime.length - 3
    );

    return (
      <div className="col-12 md:col-12">
        <Dialog
          header={t("screentext.screen-text-passenger-booking-details-header")}
          visible={bookingDetailsVisible}
          maximizable
          style={{ width: "90vw" }}
          onHide={() => setBookingDetailsVisible(false)}
        >
          <div
            style={{
              paddingLeft: "20px",
              paddingRight: "20px",
            }}
          >
            <div className="font-bold">
              <i className="pi pi-calendar" />
              <span className="mr-2"> {pickupDate}</span>
              <i className="pi pi-clock" />
              <span className="mr-2"> {pickupTime}</span>
              <span className="mr-2">
                TicketID: {bookingDetails.current.TicketID}
              </span>
            </div>
            <p className="mt-3 mb-0">
              <span className="font-bold">
                {t("screentext.screen-text-from")}
              </span>{" "}
              <i
                className="pi pi-map-marker"
                onClick={() =>
                  viewLocationOnGoogleMaps(bookingDetails.current.PickupFrom)
                }
                style={{ cursor: "pointer" }}
              />{" "}
              {bookingDetails.current.PickupFrom}
            </p>
            <p className="mt-2 mb-0">
              <span className="font-bold">
                {t("screentext.screen-text-to")}
              </span>{" "}
              <i
                className="pi pi-map-marker"
                onClick={() =>
                  viewLocationOnGoogleMaps(bookingDetails.current.TransferTo)
                }
                style={{ cursor: "pointer" }}
              />{" "}
              {bookingDetails.current.TransferTo}
            </p>
          </div>
        </Dialog>
      </div>
    );
  };

  //--------------------------------------------------------------
  //--- Returns JSX code for "PassengerAcceptBookingsScreen" -----
  //--------------------------------------------------------------
  return (
    <>
      <div
        id="jump-start"
        className="grid p-fluid"
        style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "30px" }}
      >         
        {bookingDetails.current !== null ? showBookingDetails() : null}

        <div className="col-12 md:col-12">
          <div className="text-900 font-medium text-900 text-xl mb-3">
            {t("screentext.screen-text-passenger-accepted-list-header")}
          </div>
        </div>

        {/* Begin: Search field */}
        <div className="col-12 md:col-2 lg:col-2">
          <span className="p-float-label">
            <InputText
              id="year"
              name="year"
              value={year}
              maxlength="4"
              onChange={handleChange}
            />
            <label htmlFor="search">{t("formular.placeholder-year")}</label>
          </span>
        </div>

        <div className="col-12 md:col-2 lg:col-2">
          <span className="p-float-label">
            <Dropdown
              id="month"
              name="month"
              optionLabel="name"
              value={selectedMonth}
              onChange={handleChange}
              options={month}
            />
            <label htmlFor="search">{t("formular.placeholder-month")}</label>
          </span>
        </div>

        {/*
        <div className="col-12 md:col-8 lg:col-8">
          <span className="p-float-label p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              id="search"
              name="search"
              maxlength="250"
              onChange={handleChange}
            />
            <label htmlFor="search">
              {t("formular.placeholder-search-ticketid")}
            </label>
          </span>
        </div>
        */}
        {/* End: Search field */}

        {numberOfDatasets > 0 ? (
          <div className="col-12 md:col-12 font-bold text-primary-500 ">
            {t("screentext.screen-text-number-bookings")} {totalRecords}
          </div>
        ) : null}

        {serverPending ? (
          <div className="col-12 md:col-12 flex align-items-center">
            <ProgressSpinner style={{ width: "60px", height: "60px" }} />
            <div style={{ height: "250px" }} />
          </div>
        ) : (
          <>
            {/* Begin: List of bookings */}
            <div className="col-12 md:col-12">
              <ul className="list-none p-0 m-0">
                {console.log(filteredList)}
                {filteredList.map((item, index) => showBookings(item, index))}
              </ul>
            </div>
            {/* End: List of bookings */}
          </>
        )}

        {totalRecords > 0 ? (
          <div className="flex col-12 md:col-12 align-items-center justify-content-center">
            <Paginator
              first={first}
              rows={rows}
              totalRecords={totalRecords}
              rowsPerPageOptions={[100, 150, 200, 300, 500]}
              onPageChange={handlePageChange}
            />
          </div>
        ) : null}
      </div>
      <div style={{ height: "100px" }}>
        <p>{""}</p>
      </div>
    </>
  );
};

export default PassengerAcceptBookingsScreen;
